import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Snackbar,
  TextField,
  Box
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import TwoFactorAuth from "../../molecules/TwoFactorAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { getTOTPUri, verify2FA } from "../../../api/services/services";
import successImg from "../../../imgs/succes.svg";
import { useDispatch } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./style.css";

const TwoFactorAuthPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const [qrData, setQrData] = useState("");
  const [authKey, setAuthKey] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [verify, setVerify] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [success, setSuccess] = useState(false);

  const inputRefs = React.useRef([]);

  const handleChange = (index, value) => {
    if (value.match(/^\d{0,1}$/)) {
      const updatedDigits = [...digits];
      updatedDigits[index] = value;
      setDigits(updatedDigits);
      if (index < 5 && value !== "") {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && digits[index] === "" && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedData.length; i++) {
        newDigits[i] = pastedData[i];
      }
      setDigits(newDigits);
      
      const nextIndex = Math.min(pastedData.length, 5);
      if (inputRefs.current[nextIndex]) {
         inputRefs.current[nextIndex].focus();
      }
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Confirming 2FA
  const confirm2Fa = () => {
    let code = digits.join("");
    
    const body = {
      code: code,
    };

    const activeToken = state?.token || localStorage.getItem("fa-token");
    verify2FA(activeToken, body)
      .then((response) => {
        setSuccess(true);
        localStorage.setItem("shelLoginToken", response?.data?.data?.token);
        localStorage.setItem(
          "shelUserData",
          JSON.stringify(response?.data?.data?.admin)
        );

        const currentTime = new Date();
        localStorage.setItem("loginTime", currentTime);

        localStorage.removeItem("isAuthPending");
      })
      .catch((error) => {
        console.error("Error ", error?.response?.data);
        if (error?.response?.data?.statusCode == 1002) {
          setSnackbarMessage("Token has been expired");
          setOpenSnackbar(true);
        } else {
          setSnackbarMessage("Invalid Code. Please try again.");
          setOpenSnackbar(true);
          // Optional: Clear digits or keep them for user correction
          // setDigits(["", "", "", "", "", ""])
        }
      });
  };

  useEffect(() => {
    let val = localStorage.getItem('fa-token') || state?.token;

    getTOTPUri(val)
      .then((response) => {
        setQrData(response?.data?.data?.toTpURI);
        setAuthKey(response?.data?.data?.formattedKey);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error ", error?.response?.data);
        if (error?.response?.data?.statusCode == 1002) {
          setSnackbarMessage("Token has been expired");
          setOpenSnackbar(true);
        } else {
          setSnackbarMessage(error?.response?.data?.message || "Failed to load QR code");
          setOpenSnackbar(true);
        }
        localStorage.removeItem("isAuthPending");
      });
  }, []);

  const isConfirmButtonDisabled = digits.some(digit => digit === "");

  return (
    <div className="login-split-container">
      {/* ... (keep brand-panel) ... */}
      <div className="brand-panel">
        <div className="brand-content">
          <Typography variant="h1" className="brand-title">
            Shelozon Digital
          </Typography>
          <Typography variant="h5" className="brand-tagline">
             Secure protection for your data.
          </Typography>
        </div>
      </div>

      {/* Right Panel: The Functional Form */}
      <div className="form-panel">
        <Container maxWidth="xs" className="form-container">
            {!success ? (
              <>
                {!verify ? (
                   <>
                      <div className="form-header">
                        <Typography variant="h4" className="form-title">
                           Enable Authenticator
                        </Typography>
                        <Typography variant="body1" className="form-desc">
                           Authenticator app should be installed on your device to use 2FA
                        </Typography>
                      </div>

                      {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                          <CircularProgress size={30} />
                        </Box>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                            <TwoFactorAuth qrData={qrData} authKey={authKey} />
                        </div>
                      )}

                      <Button
                        type="button"
                        variant="contained"
                        className="submit-btn"
                        onClick={() => setVerify(true)}
                        fullWidth
                      >
                        Next
                      </Button>
                   </>
                ) : (
                   <>
                       <div className="form-header">
                        <Typography variant="h4" className="form-title">
                           Verify Code
                        </Typography>
                        <Typography variant="body1" className="form-desc">
                           Enter the 6-digit code from your authenticator app
                        </Typography>
                      </div>
                      
                      <div className="d-flex align-items-center justify-content-center mb-4 pin-input-container">
                        {digits.map((digit, index) => (
                          <TextField
                            key={index}
                            inputRef={(el) => (inputRefs.current[index] = el)}
                            variant="outlined"
                            size="small"
                            type="text"
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            className="pin-field"
                            sx={{
                              width: isSmallScreen ? "45px" : "55px",
                              '& input': { textAlign: 'center', padding: '12px 0', fontSize: '24px', fontWeight: 'bold' }
                            }}
                          />
                        ))}
                      </div>

                      <Button
                        type="button"
                        variant="contained"
                        className={
                          isConfirmButtonDisabled
                            ? "disabled-btn text-capitalize"
                            : "submit-btn text-capitalize"
                        }
                        onClick={confirm2Fa}
                        disabled={isConfirmButtonDisabled}
                        fullWidth
                      >
                        Confirm
                      </Button>
                   </>
                )}
              </>
            ) : (
             <div className="success-container">
                <img
                    src={successImg}
                    alt="success"
                    width="84"
                    height="84"
                    className="mb-4"
                />
                <Typography variant="h5" className="success-title">
                    Done!
                </Typography>
                <Typography variant="body1" className="form-desc mb-5">
                    Your account security has been updated.
                </Typography>
                <Button
                  type="submit"
                  variant="contained"
                  className="submit-btn text-capitalize"
                  onClick={() => {
                    setTimeout(() => {
                       window.location.pathname = "/";
                    }, 500);
                  }}
                  fullWidth
                >
                  Continue to App
                </Button>
             </div>
            )}
        </Container>
      </div>
    </div>
  );

};

export default TwoFactorAuthPage;
